import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import scheduleTagApi from "../../../api/scheduleTagApi";

const EMPTY_TAG_DEFINITIONS = [];

export const scheduleTagQueryKeys = {
    all: ["scheduleTagDefinitions"],
    list: (includeInactive) => [...scheduleTagQueryKeys.all, { includeInactive: Boolean(includeInactive) }],
};

async function fetchScheduleTags(includeInactive) {
    const response = await scheduleTagApi.list({ includeInactive });
    return Array.isArray(response?.data?.items) ? response.data.items : EMPTY_TAG_DEFINITIONS;
}

export function useScheduleTags({ includeInactive = false, enabled = true } = {}) {
    const queryClient = useQueryClient();
    const query = useQuery(
        scheduleTagQueryKeys.list(includeInactive),
        () => fetchScheduleTags(includeInactive),
        {
            enabled,
            staleTime: 5 * 60 * 1000,
            cacheTime: 30 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    );

    const definitions = query.data || EMPTY_TAG_DEFINITIONS;
    const definitionMap = useMemo(
        () => new Map(definitions.map((definition) => [String(definition.tag_name || "").trim().toLocaleLowerCase(), definition])),
        [definitions],
    );
    const refresh = () => queryClient.invalidateQueries(scheduleTagQueryKeys.all);
    const createMutation = useMutation((data) => scheduleTagApi.create(data), { onSuccess: refresh });
    const updateMutation = useMutation(({ tagId, data }) => scheduleTagApi.update(tagId, data), { onSuccess: refresh });

    return {
        definitions,
        definitionMap,
        activeDefinitions: definitions.filter((definition) => definition.is_active),
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        loadError: query.error || null,
        refetch: query.refetch,
        refresh,
        createTag: createMutation.mutateAsync,
        updateTag: updateMutation.mutateAsync,
        isSaving: createMutation.isLoading || updateMutation.isLoading,
    };
}

export default useScheduleTags;
